import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Product } from "@/hooks/useProducts";

const COMPARISON_KEY = "product_comparison";
const MAX_COMPARISON_ITEMS = 4;

interface ProductComparisonContextType {
  comparisonProducts: Product[];
  addToComparison: (product: Product) => void;
  removeFromComparison: (productId: string) => void;
  clearComparison: () => void;
  isInComparison: (productId: string) => boolean;
  canAddMore: boolean;
}

const ProductComparisonContext = createContext<ProductComparisonContextType | undefined>(undefined);

export function ProductComparisonProvider({ children }: { children: ReactNode }) {
  const [comparisonProducts, setComparisonProducts] = useState<Product[]>([]);

  // Load comparison products from localStorage on component mount
  useEffect(() => {
    const stored = localStorage.getItem(COMPARISON_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setComparisonProducts(parsed);
        } else {
          // Reset if invalid format
          localStorage.removeItem(COMPARISON_KEY);
        }
      } catch (error) {
        console.error("Failed to parse comparison products:", error);
        // Clear corrupted data
        localStorage.removeItem(COMPARISON_KEY);
      }
    }
  }, []);

  const addToComparison = (product: Product) => {
    // Check if product is already in comparison
    if (isInComparison(product.id)) {
      return; // Don't add duplicates
    }
    
    // Add new product to the list (up to MAX_COMPARISON_ITEMS)
    const updated = [
      ...comparisonProducts,
      product
    ].slice(0, MAX_COMPARISON_ITEMS);
    
    setComparisonProducts(updated);
    localStorage.setItem(COMPARISON_KEY, JSON.stringify(updated));
  };

  const removeFromComparison = (productId: string) => {
    const updated = comparisonProducts.filter(p => p.id !== productId);
    setComparisonProducts(updated);
    
    // Update localStorage or remove if empty
    if (updated.length > 0) {
      localStorage.setItem(COMPARISON_KEY, JSON.stringify(updated));
    } else {
      localStorage.removeItem(COMPARISON_KEY);
    }
  };

  const clearComparison = () => {
    setComparisonProducts([]);
    localStorage.removeItem(COMPARISON_KEY);
  };

  const isInComparison = (productId: string) => {
    return comparisonProducts.some(p => p.id === productId);
  };

  const value = {
    comparisonProducts,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isInComparison,
    canAddMore: comparisonProducts.length < MAX_COMPARISON_ITEMS,
  };

  return (
    <ProductComparisonContext.Provider value={value}>
      {children}
    </ProductComparisonContext.Provider>
  );
}

export function useProductComparison() {
  const context = useContext(ProductComparisonContext);
  if (context === undefined) {
    throw new Error("useProductComparison must be used within a ProductComparisonProvider");
  }
  return context;
}
